import React from "react"

interface TransitionData {
  from: string
  to: string
  sum: number
  count: number
  avgMs: number
}

interface PeriodData {
  period: string
  transitions: TransitionData[]
  totals: {
    totalApplicationsNew: number
    totalApplicationsDoneNew: number
  }
}

interface CampaignTransitionsTableProps {
  data: PeriodData[]
  viewMode: "day" | "week" | "month"
  onViewModeChange: (mode: "day" | "week" | "month") => void
}

const CampaignTransitionsTable: React.FC<CampaignTransitionsTableProps> = ({
  data,
  viewMode,
  onViewModeChange,
}) => {
  const formatDate = (period: string) => {
    try {
      const date = new Date(period)

      if (viewMode === "week") {
        const startOfWeek = new Date(date)
        const day = startOfWeek.getDay()
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
        startOfWeek.setDate(diff)

        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)

        return `${startOfWeek.toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
        })} - ${endOfWeek.toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
        })}`
      } else if (viewMode === "month") {
        return date.toLocaleDateString("tr-TR", {
          month: "long",
          year: "numeric",
        })
      } else {
        return date.toLocaleDateString("tr-TR", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
        })
      }
    } catch {
      return period
    }
  }

  const formatDuration = (durationMs: number) => {
    if (durationMs === 0) {
      return "0"
    }

    const durationSeconds = durationMs / 1000
    if (durationSeconds < 60) {
      return `${Math.round(durationSeconds)}s`
    } else if (durationSeconds < 3600) {
      return `${Math.round(durationSeconds / 60)}dk`
    } else if (durationSeconds < 86400) {
      return `${Math.round(durationSeconds / 3600)}sa`
    } else {
      return `${Math.round(durationSeconds / 86400)}g`
    }
  }

  const transitionRules: Record<string, { validFromStates: string[]; validRoles: string[] }> = {
    invite_success: { validFromStates: ["invite_new"], validRoles: ["user", "admin"] },
    account_rejected: { validFromStates: ["first_application"], validRoles: ["brand", "admin"] },
    waiting_address: {
      validFromStates: [
        "first_application",
        "invite_success",
        "address_rejected",
        "address_to_approve",
      ],
      validRoles: ["brand", "admin"],
    },
    address_to_approve: {
      validFromStates: ["waiting_address", "address_rejected"],
      validRoles: ["user", "brand", "admin"],
    },
    address_rejected: {
      validFromStates: ["address_to_approve"],
      validRoles: ["brand", "admin"],
    },
    waiting_content: {
      validFromStates: ["first_application", "invite_success", "address_to_approve"],
      validRoles: ["brand", "admin"],
    },
    ugc_rejected: {
      validFromStates: ["ugc_content"],
      validRoles: ["brand", "admin"],
    },
    ugc_content: {
      validFromStates: ["waiting_content", "ugc_rejected"],
      validRoles: ["user", "admin"],
    },
    content_to_share: {
      validFromStates: ["waiting_content", "content_rejected"],
      validRoles: ["user", "admin"],
    },
    content_rejected: {
      validFromStates: ["content_to_share"],
      validRoles: ["brand", "admin"],
    },
    content_approved: { validFromStates: ["content_to_share"], validRoles: ["brand", "admin"] },
    brand_canceled: {
      validFromStates: [
        "invite_new",
        "invite_success",
        "waiting_address",
        "waiting_content",
        "content_to_share",
        "content_rejected",
        "content_shared",
        "ugc_content",
        "ugc_rejected",
      ],
      validRoles: ["brand", "admin"],
    },
    user_canceled: {
      validFromStates: [
        "invite_new",
        "invite_success",
        "first_application",
        "content_offered",
        "waiting_content",
        "content_rejected",
        "content_approved",
        "ugc_content",
        "ugc_rejected",
      ],
      validRoles: ["user", "admin"],
    },
    content_shared: {
      validFromStates: ["content_approved"],
      validRoles: ["user", "admin"],
    },
    application_done: {
      validFromStates: ["content_shared", "content_to_share", "ugc_content"],
      validRoles: ["brand", "admin"],
    },
    application_timeout: {
      validFromStates: ["waiting_content"],
      validRoles: ["brand", "admin"],
    },
  }

  const getTransitionCategory = (from: string, to: string) => {
    const rule = transitionRules[to]
    if (!rule) {
      return "other"
    }

    if (!rule.validFromStates.includes(from)) {
      return "other"
    }

    if (rule.validRoles.includes("user") && !rule.validRoles.includes("brand")) {
      return "user"
    } else if (rule.validRoles.includes("brand") && !rule.validRoles.includes("user")) {
      return "company"
    } else if (rule.validRoles.includes("user") && rule.validRoles.includes("brand")) {
      return "both"
    }

    return "other"
  }

  const getAllTransitions = () => {
    const userTransitions = new Set<string>()
    const companyTransitions = new Set<string>()
    const bothTransitions = new Set<string>()
    const otherTransitions = new Set<string>()

    data.forEach((period) => {
      period.transitions.forEach((transition) => {
        const key = `${transition.from} => ${transition.to}`
        const category = getTransitionCategory(transition.from, transition.to)

        switch (category) {
          case "user":
            userTransitions.add(key)
            break
          case "company":
            companyTransitions.add(key)
            break
          case "both":
            bothTransitions.add(key)
            break
          default:
            otherTransitions.add(key)
        }
      })
    })

    return {
      user: Array.from(userTransitions).sort(),
      company: Array.from(companyTransitions).sort(),
      both: Array.from(bothTransitions).sort(),
      other: Array.from(otherTransitions).sort(),
    }
  }

  const allTransitions = getAllTransitions()

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-500 rounded-lg p-2">
              <span className="text-white text-lg">📋</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Kampanya Geçiş Detayları</h2>
          </div>

          {/* View Mode Selector */}
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Görünüm Modu:</span>
            <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => onViewModeChange("day")}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === "day"
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="mr-1">👁️</span>
                Günlük Görünüm
              </button>
              <button
                onClick={() => onViewModeChange("week")}
                className={`px-3 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                  viewMode === "week"
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="mr-1">📅</span>
                Haftalık Görünüm
              </button>
              <button
                onClick={() => onViewModeChange("month")}
                className={`px-3 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                  viewMode === "month"
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="mr-1">📊</span>
                Aylık Görünüm
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="overflow-x-auto overflow-y-auto relative"
        style={{ maxHeight: "100vh", minWidth: "100%" }}
      >
        <table
          className="min-w-full"
          style={{ minWidth: `${Math.max(600, data.length * 200 + 200)}px` }}
        >
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-1 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                Geçiş Detayları
              </th>
              {data.map((period) => (
                <th
                  key={period.period}
                  className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200 bg-gray-50"
                >
                  {formatDate(period.period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* User Side Transitions */}
            {allTransitions.user.length > 0 && (
              <>
                <tr>
                  <td
                    className="bg-blue-50 text-left px-1 py-3 text-sm font-bold text-blue-800 uppercase tracking-wider border-b-2 border-blue-200 sticky left-0 z-5 bg-blue-50 border-r border-blue-200"
                    style={{ minWidth: "180px", width: "180px" }}
                  >
                    Kullanıcı Tarafı
                  </td>
                  <td colSpan={data.length} className="bg-blue-50 border-b-2 border-blue-200"></td>
                </tr>
                {allTransitions.user.map((transitionKey) => (
                  <tr key={transitionKey} className="hover:bg-gray-50">
                    <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                      <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                        {transitionKey}
                      </span>
                    </td>
                    {data.map((period) => {
                      const transition = period.transitions.find(
                        (t) => `${t.from} => ${t.to}` === transitionKey,
                      )

                      if (!transition) {
                        return (
                          <td
                            key={period.period}
                            className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                            style={{ minWidth: "180px" }}
                          >
                            <div className="text-sm text-gray-400">-</div>
                          </td>
                        )
                      }

                      return (
                        <td
                          key={period.period}
                          className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                          style={{ minWidth: "180px" }}
                        >
                          <div className="text-sm">
                            <div className="text-gray-900">
                              Geçirilen süre: {formatDuration(transition.avgMs)}
                            </div>
                            <div className="text-gray-900">Tekrar sayısı: {transition.count}</div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* Company Side Transitions */}
            {allTransitions.company.length > 0 && (
              <>
                <tr>
                  <td
                    className="bg-orange-50 text-left px-1 py-3 text-sm font-bold text-orange-800 uppercase tracking-wider border-b-2 border-orange-200 sticky left-0 z-5 bg-orange-50 border-r border-orange-200"
                    style={{ minWidth: "180px", width: "180px" }}
                  >
                    Firma Tarafı
                  </td>
                  <td
                    colSpan={data.length}
                    className="bg-orange-50 border-b-2 border-orange-200"
                  ></td>
                </tr>
                {allTransitions.company.map((transitionKey) => (
                  <tr key={transitionKey} className="hover:bg-gray-50">
                    <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                      <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                        {transitionKey}
                      </span>
                    </td>
                    {data.map((period) => {
                      const transition = period.transitions.find(
                        (t) => `${t.from} => ${t.to}` === transitionKey,
                      )

                      if (!transition) {
                        return (
                          <td
                            key={period.period}
                            className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                            style={{ minWidth: "180px" }}
                          >
                            <div className="text-sm text-gray-400">-</div>
                          </td>
                        )
                      }

                      return (
                        <td
                          key={period.period}
                          className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                          style={{ minWidth: "180px" }}
                        >
                          <div className="text-sm">
                            <div className="text-gray-900">
                              Geçirilen süre: {formatDuration(transition.avgMs)}
                            </div>
                            <div className="text-gray-900">Tekrar sayısı: {transition.count}</div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* Both User and Company Transitions */}
            {allTransitions.both.length > 0 && (
              <>
                <tr>
                  <td
                    className="bg-green-50 text-left px-1 py-3 text-sm font-bold text-green-800 uppercase tracking-wider border-b-2 border-green-200 sticky left-0 z-5 bg-green-50 border-r border-green-200"
                    style={{ minWidth: "180px", width: "180px" }}
                  >
                    Her İki Taraf
                  </td>
                  <td
                    colSpan={data.length}
                    className="bg-green-50 border-b-2 border-green-200"
                  ></td>
                </tr>
                {allTransitions.both.map((transitionKey) => (
                  <tr key={transitionKey} className="hover:bg-gray-50">
                    <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                      <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                        {transitionKey}
                      </span>
                    </td>
                    {data.map((period) => {
                      const transition = period.transitions.find(
                        (t) => `${t.from} => ${t.to}` === transitionKey,
                      )

                      if (!transition) {
                        return (
                          <td
                            key={period.period}
                            className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                            style={{ minWidth: "180px" }}
                          >
                            <div className="text-sm text-gray-400">-</div>
                          </td>
                        )
                      }

                      return (
                        <td
                          key={period.period}
                          className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                          style={{ minWidth: "180px" }}
                        >
                          <div className="text-sm">
                            <div className="text-gray-900">
                              Geçirilen süre: {formatDuration(transition.avgMs)}
                            </div>
                            <div className="text-gray-900">Tekrar sayısı: {transition.count}</div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* Other Transitions */}
            {allTransitions.other.length > 0 && (
              <>
                <tr>
                  <td
                    className="bg-gray-50 text-left px-1 py-3 text-sm font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 sticky left-0 z-5 bg-gray-50 border-r border-gray-200"
                    style={{ minWidth: "180px", width: "180px" }}
                  >
                    Diğer
                  </td>
                  <td colSpan={data.length} className="bg-gray-50 border-b-2 border-gray-200"></td>
                </tr>
                {allTransitions.other.map((transitionKey) => (
                  <tr key={transitionKey} className="hover:bg-gray-50">
                    <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                      <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                        {transitionKey}
                      </span>
                    </td>
                    {data.map((period) => {
                      const transition = period.transitions.find(
                        (t) => `${t.from} => ${t.to}` === transitionKey,
                      )

                      if (!transition) {
                        return (
                          <td
                            key={period.period}
                            className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                            style={{ minWidth: "180px" }}
                          >
                            <div className="text-sm text-gray-400">-</div>
                          </td>
                        )
                      }

                      return (
                        <td
                          key={period.period}
                          className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200"
                          style={{ minWidth: "180px" }}
                        >
                          <div className="text-sm">
                            <div className="text-gray-900">
                              Geçirilen süre: {formatDuration(transition.avgMs)}
                            </div>
                            <div className="text-gray-900">Tekrar sayısı: {transition.count}</div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CampaignTransitionsTable
